import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { appRoutes } from './pages/Root.route';
import { queryClient } from './lib/queryClient';
import { installConsoleLogBuffer } from './lib/consoleLogBuffer';
import { initMaieShell } from './config/maieShell';
import './index.css';
/* The design kit's stylesheet, app-wide.
 *
 * Safe to load globally because every rule in it is scoped under `.maie` —
 * including its `*` reset, which is exactly why nothing outside a `.maie`
 * subtree is touched. `scripts/audit-kit.mjs` fails the build if a rule ever
 * escapes that scope, and it caught one that had (`.maiebar`, via a prefix
 * match). Loaded here rather than in the shell so the gallery and the app
 * cannot drift onto two different copies. */
import './components/kit/fonts.css';
import './components/kit/kit.css';
import './components/kit/kit-extras.css';

// Install before the app mounts so render errors that happen before the
// FeedbackWidget useEffect runs still land in the buffer for the next
// reload's bug report.
installConsoleLogBuffer();

// Before the router: `?maie=1` has to be captured while the query string still
// exists. An unauthenticated deep link redirects to /login and loses it.
initMaieShell();

// Data router (createBrowserRouter) — required for useBlocker/usePrompt, which
// power the encounter review page's unsaved-changes guard. Under the old
// <BrowserRouter> those hooks threw via UNSAFE_invariant. The route tree itself
// (incl. the app shell, auth gates, and ~120 routes) lives in Root.route.tsx.
// Created once at module scope so StrictMode's double-invoke can't rebuild it.
const router = createBrowserRouter(appRoutes, {
  future: {
    v7_relativeSplatPath: true,
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </QueryClientProvider>
  </StrictMode>
);
